import React, { useState, useEffect, useRef } from 'react';
import ReactSliderComponent from 'react-slider';
import api from './api';

const ReactSlider = (props) => {
  let TargetComponent = ReactSliderComponent;
  if (ReactSliderComponent && ReactSliderComponent.default) {
    TargetComponent = ReactSliderComponent.default;
  }
  if (!TargetComponent || typeof TargetComponent !== 'function') {
    return (
      <div className="alert alert-danger font-monospace small">
        ⚠️ Slider driver loaded as an invalid type ({typeof TargetComponent}).
      </div>
    );
  }
  return <TargetComponent {...props} />;
};

const SliderPage = () => {
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Playback Simulation State
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(2000); // default 2s per step
  
  // Dynamic Filters
  const [selectedIp, setSelectedIp] = useState('all');
  const [selectedPage, setSelectedPage] = useState('all');
  
  const timerRef = useRef(null);

  useEffect(() => {
    fetchClicks();
  }, []);

  const fetchClicks = async () => {
    try {
      // Fetch up to 500 events to make the simulation richer
      const response = await api.get('/clicks?limit=500');
      // Sort chronologically (oldest first) so playback moves forward in time
      const sorted = (response.data.data.clicks || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setClicks(sorted);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clicks:', error);
      setLoading(false);
    }
  };

  // Filter clicks dynamically
  const filteredClicks = clicks.filter(click => {
    const matchIp = selectedIp === 'all' || click.ipAddress === selectedIp;
    const matchPage = selectedPage === 'all' || click.pageTitle === selectedPage;
    return matchIp && matchPage;
  });

  // Handle Auto-Play simulation
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= filteredClicks.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, playSpeed);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, playSpeed, filteredClicks.length]);

  // Reset index when filters change
  useEffect(() => {
    setCurrentIndex(0);
    setIsPlaying(false);
  }, [selectedIp, selectedPage]);

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="small text-muted">Loading historical timeline...</p>
      </div>
    );
  }

  if (clicks.length === 0) {
    return (
      <div className="card-premium text-center py-5">
        <p className="text-muted mb-0">No tracking data available for slider timeline</p>
      </div>
    );
  }

  // Get unique IPs (sorted by click count descending) and Pages for filter lists
  const ipClickCounts = {};
  clicks.forEach(c => {
    if (c.ipAddress) {
      ipClickCounts[c.ipAddress] = (ipClickCounts[c.ipAddress] || 0) + 1;
    }
  });
  const sortedUniqueIps = Object.keys(ipClickCounts).sort((a, b) => ipClickCounts[b] - ipClickCounts[a]);
  const uniquePages = [...new Set(clicks.map(c => c.pageTitle).filter(Boolean))];

  // Helper to generate friendly name
  const getFriendlyNameForIp = (ipAddr) => {
    const match = clicks.find(c => c.ipAddress === ipAddr && c.realName);
    if (match && match.realName) return match.realName;
    return ipAddr ? ipAddr.replace('::ffff:', '') : '127.0.0.1';
  };

  const currentClick = filteredClicks[currentIndex];

  // Get the surrounding timeline path items (2 before, 2 after)
  const getJourneyPath = () => {
    if (filteredClicks.length === 0) return [];
    const pathItems = [];
    const start = Math.max(0, currentIndex - 2);
    const end = Math.min(filteredClicks.length - 1, currentIndex + 2);
    
    for (let i = start; i <= end; i++) {
      pathItems.push({
        index: i,
        isActive: i === currentIndex,
        click: filteredClicks[i]
      });
    }
    return pathItems;
  };

  const journeyPath = getJourneyPath();

  return (
    <div className="d-flex flex-column gap-4">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
        <div>
          <h2 className="h4 text-dark mb-1">Interaction Journey Simulator</h2>
          <p className="small text-muted mb-0">Visual playback of user click flows across pages chronologically.</p>
        </div>

        {/* Playback Speed selector */}
        {filteredClicks.length > 0 && (
          <div className="btn-group btn-group-sm bg-light p-1 rounded">
            <button
              onClick={() => setPlaySpeed(3000)}
              className={`btn btn-sm ${playSpeed === 3000 ? 'btn-primary' : 'btn-light text-muted'}`}
            >
              Slow (3s)
            </button>
            <button
              onClick={() => setPlaySpeed(2000)}
              className={`btn btn-sm ${playSpeed === 2000 ? 'btn-primary' : 'btn-light text-muted'}`}
            >
              Normal (2s)
            </button>
            <button
              onClick={() => setPlaySpeed(1000)}
              className={`btn btn-sm ${playSpeed === 1000 ? 'btn-primary' : 'btn-light text-muted'}`}
            >
              Fast (1s)
            </button>
          </div>
        )}
      </div>

      {/* Control Filter Bar */}
      <div className="card-premium py-3 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          {/* Visitor Filter */}
          <div>
            <span className="small text-muted d-block mb-1 text-uppercase font-weight-bold" style={{ fontSize: '10px' }}>Filter Visitor</span>
            <select
              value={selectedIp}
              onChange={(e) => setSelectedIp(e.target.value)}
              className="form-select form-select-sm"
              style={{ width: '200px' }}
            >
              <option value="all">All Visitors</option>
              {sortedUniqueIps.map(ip => (
                <option key={ip} value={ip}>
                  {getFriendlyNameForIp(ip)} ({ip}) — {ipClickCounts[ip]} clicks
                </option>
              ))}
            </select>
          </div>

          {/* Page Filter */}
          <div>
            <span className="small text-muted d-block mb-1 text-uppercase font-weight-bold" style={{ fontSize: '10px' }}>Filter Page</span>
            <select
              value={selectedPage}
              onChange={(e) => setSelectedPage(e.target.value)}
              className="form-select form-select-sm"
              style={{ width: '200px' }}
            >
              <option value="all">All Pages</option>
              {uniquePages.map(page => (
                <option key={page} value={page}>{page}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick summary stats */}
        <div className="text-sm-end">
          <span className="badge bg-primary d-block mb-1">
            {filteredClicks.length} Events Matching
          </span>
          <span className="small text-muted d-block" style={{ fontSize: '10px' }}>
            from {clicks.length} total events
          </span>
        </div>
      </div>

      {filteredClicks.length === 0 ? (
        <div className="card-premium py-5 text-center text-muted">
          No events match the selected filters. Change filters above.
        </div>
      ) : (
        <>
          {/* Timeline Map / Journey Stepper */}
          <div className="card-premium">
            <h3 className="h6 text-muted mb-3 text-uppercase font-weight-bold" style={{ fontSize: '12px' }}>Interaction Flow Pathway</h3>
            <div className="row g-3">
              {journeyPath.map((item, idx) => {
                const isPageView = item.click.eventType === 'page_view';
                return (
                  <div
                    key={idx}
                    onClick={() => setCurrentIndex(item.index)}
                    className="col"
                    style={{ minWidth: '160px' }}
                  >
                    <div
                      className={`p-3 rounded border text-start position-relative h-100 ${
                        item.isActive
                          ? 'bg-light border-primary shadow-sm'
                          : 'bg-white border-light hover-shadow'
                      }`}
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className={`badge ${isPageView ? 'bg-primary' : 'bg-success'}`} style={{ fontSize: '9px' }}>
                          {item.click.eventType || 'click'}
                        </span>
                        <span className="text-muted font-monospace" style={{ fontSize: '9px' }}>
                          Step {item.index + 1}
                        </span>
                      </div>
                      <div className="small font-weight-bold text-dark text-truncate" title={item.click.buttonName}>
                        {isPageView ? 'Page View' : item.click.buttonName}
                      </div>
                      <div className="text-muted text-truncate mt-1" style={{ fontSize: '10px' }} title={item.click.pageTitle}>
                        {item.click.pageTitle}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Active Detail Display */}
          <div className="row g-4">
            {/* Left detail card */}
            <div className="col-lg-8">
              <div className="card-premium h-100">
                <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                  <span className="badge-indigo">
                    Active Event {currentIndex + 1} of {filteredClicks.length}
                  </span>
                  <span className="small text-muted font-monospace">
                    ID: {currentClick.id ? currentClick.id.substring(0, 12) : 'N/A'}
                  </span>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '10px' }}>Interaction Action</label>
                    <p className="h5 text-dark font-weight-bold mb-0">
                      {currentClick.eventType === 'page_view' ? (
                        <span className="text-primary">Page View</span>
                      ) : (
                        <span className="text-success">Clicked: "{currentClick.buttonName}"</span>
                      )}
                    </p>
                  </div>

                  <div className="col-md-6">
                    <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '10px' }}>Target Page URL</label>
                    <p className="small text-dark break-all font-monospace mb-0">
                      {currentClick.url}
                    </p>
                  </div>

                  <div className="col-md-6">
                    <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '10px' }}>Page Title</label>
                    <p className="small text-dark mb-0">{currentClick.pageTitle || 'N/A'}</p>
                  </div>

                  <div className="col-md-6">
                    <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '10px' }}>Timestamp</label>
                    <p className="small text-dark font-monospace mb-0">
                      {new Date(currentClick.timestamp).toLocaleString()}
                    </p>
                  </div>

                  {currentClick.linkUrl && (
                    <div className="col-12 border-t pt-3">
                      <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '10px' }}>Target Navigation URL</label>
                      <p className="small text-primary font-monospace bg-light p-2 rounded border mb-0">
                        {currentClick.linkUrl}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right sidebar info */}
            <div className="col-lg-4">
              <div className="card-premium h-100">
                <h3 className="h6 text-muted border-bottom pb-3 mb-3 text-uppercase font-weight-bold" style={{ fontSize: '12px' }}>
                  Visitor Fingerprint
                </h3>

                <div className="d-flex flex-column gap-3">
                  <div>
                    <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '9px' }}>Visitor Nickname</label>
                    <p className="small text-primary font-weight-bold mb-0">{getFriendlyNameForIp(currentClick.ipAddress)}</p>
                  </div>

                  <div>
                    <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '9px' }}>System IP Address</label>
                    <p className="small text-dark font-monospace mb-0">{currentClick.ipAddress}</p>
                  </div>

                  <div>
                    <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '9px' }}>Platform User-Agent</label>
                    <p className="small text-muted font-monospace text-truncate mb-0" title={currentClick.userAgent}>
                      {currentClick.userAgent}
                    </p>
                  </div>

                  <div>
                    <label className="text-uppercase text-muted d-block small mb-1" style={{ fontSize: '9px' }}>Geographic Origin</label>
                    <p className="small text-dark mb-0">
                      {currentClick.country || 'India'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Slider Player & Controls */}
          <div className="card-premium">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 mb-4">
              <div className="d-flex items-center gap-2">
                <button
                  onClick={() => setCurrentIndex(p => Math.max(0, p - 1))}
                  disabled={currentIndex === 0}
                  className="btn btn-outline-secondary btn-sm"
                  title="Previous Step"
                >
                  &#9664; Prev
                </button>

                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className={`btn btn-sm ${isPlaying ? 'btn-danger' : 'btn-primary'}`}
                >
                  {isPlaying ? 'Pause Simulation' : 'Play Simulation'}
                </button>

                <button
                  onClick={() => setCurrentIndex(p => Math.min(filteredClicks.length - 1, p + 1))}
                  disabled={currentIndex === filteredClicks.length - 1}
                  className="btn btn-outline-secondary btn-sm"
                  title="Next Step"
                >
                  Next &#9654;
                </button>
              </div>

              <div className="font-monospace small text-muted">
                Playhead Position: <span className="text-primary font-weight-bold">{currentIndex + 1}</span> / {filteredClicks.length}
              </div>
            </div>

            <div className="pt-2 px-2">
              <ReactSlider
                className="custom-slider"
                thumbClassName="thumb"
                value={currentIndex}
                min={0}
                max={filteredClicks.length - 1}
                onChange={(val) => setCurrentIndex(val)}
              />
              <div className="d-flex justify-content-between small text-muted mt-2 font-monospace" style={{ fontSize: '11px' }}>
                <span>Start (Oldest Event)</span>
                <span className="text-primary font-weight-bold">Active step: {currentIndex + 1}</span>
                <span>End (Newest Event)</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SliderPage;
